import api from './api';

// Função auxiliar para formatar data para pt-BR (DD/MM/YYYY)
function formatDateBR(dateString) {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    } catch (e) {
        return '-';
    }
}

// Função auxiliar para converter data DD/MM/YYYY para ISO
function parseDate(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexed
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day).toISOString();
    }
    // Fallback para formato ISO ou outro formato
    return new Date(dateStr).toISOString();
}

// Função auxiliar para construir dados do paciente
function buildPatientData(formData) {
    const patientData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate ? parseDate(formData.birthDate) : null,
        maritalStatus: formData.maritalStatus || 'SINGLE',
        religion: formData.religion || '',
        nationality: formData.nationality || 'Brasileiro',
        contacts: [],
        addresses: [],
        paymentMethod: formData.paymentMethod || '',
        paymentPeriod: formData.paymentPeriod || '',
        consultationValue: parseFloat(formData.consultationValue) || 0,
        issuesInvoice: formData.issuesInvoice || false
    };
    
    // Adicionar email aos contatos
    if (formData.email) {
        patientData.contacts.push({
            type: 'EMAIL',
            value: formData.email,
            primary: true
        });
    }
    
    // Adicionar telefone aos contatos
    if (formData.phone) {
        patientData.contacts.push({
            type: 'PHONE',
            value: formData.phone,
            primary: !formData.email // Primary se não houver email
        });
    }
    
    // Adicionar endereço
    if (formData.street || formData.city) {
        patientData.addresses.push({
            street: formData.street || '',
            number: formData.number || '',
            complement: formData.complement || '',
            district: formData.district || '',
            city: formData.city || '',
            state: formData.state || '',
            country: 'Brasil',
            zipCode: formData.zipCode || '',
            type: 'HOME'
        });
    }

    return patientData;
}

const patientsService = {
    // Listar todos os pacientes
    async getAll(params = {}) {
        try {
            let url = '/person/';
            const queryParams = new URLSearchParams();
            
            // Adicionar parâmetros de paginação e busca
            if (params.page) queryParams.append('page', params.page);
            if (params.pageSize) queryParams.append('pageSize', params.pageSize);
            if (params.search) queryParams.append('search', params.search);
            if (params.sortBy) queryParams.append('sortBy', params.sortBy);
            if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
            
            if (queryParams.toString()) {
                url += '?' + queryParams.toString();
            }
            
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Erro ao listar pacientes:', error);
            throw new Error('Não foi possível carregar a lista de pacientes.');
        }
    },

    // Buscar paciente por ID
    async getById(id) {
        try {
            const response = await api.get(`/person/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar paciente:', error);
            throw new Error('Não foi possível carregar os dados do paciente.');
        }
    },

    // Criar novo paciente
    async create(formData) {
        try {
            const patientData = buildPatientData(formData);
            const response = await api.post('/person/', patientData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar paciente:', error);
            throw new Error('Não foi possível cadastrar o paciente. Verifique os dados e tente novamente.');
        }
    },

    // Atualizar paciente
    async update(id, formData) {
        try {
            const patientData = buildPatientData(formData);
            const response = await api.put(`/person/${id}`, patientData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar paciente:', error);
            throw new Error('Não foi possível atualizar o paciente. Verifique os dados e tente novamente.');
        }
    },

    // Excluir paciente
    async delete(id) {
        try {
            await api.delete(`/person/${id}`);
            return { success: true };
        } catch (error) {
            console.error('Erro ao excluir paciente:', error);
            throw new Error('Não foi possível excluir o paciente.');
        }
    },

    // Buscar pacientes para select (nome simplificado)
    async getForSelect(params = {}) {
        try {
            let url = '/person/';
            const queryParams = new URLSearchParams();
            
            if (params.search) queryParams.append('search', params.search);
            
            if (queryParams.toString()) {
                url += '?' + queryParams.toString();
            }
            
            const response = await api.get(url);
            const patients = Array.isArray(response.data) ? response.data : response.data.data || [];
            
            return patients.map(patient => ({
                id: patient.id,
                name: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
                value: patient.id,
                label: `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
            }));
        } catch (error) {
            console.error('Erro ao buscar pacientes para select:', error);
            return [];
        }
    },

    // Formatadores de dados
    formatters: {
        formatDateBR,
        parseDate,
        
        // Extrair email do paciente
        getEmail(patient) {
            return patient.contacts ? 
                patient.contacts.find(c => c.type === 'EMAIL')?.value || '-' : '-';
        },
        
        // Extrair telefone do paciente
        getPhone(patient) {
            return patient.contacts ? 
                patient.contacts.find(c => c.type === 'PHONE' || c.type === 'MOBILE')?.value || '-' : '-';
        },
        
        // Nome completo do paciente
        getFullName(patient) {
            return `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
        },
        
        // Endereço completo
        getFullAddress(patient) {
            if (!patient.addresses || patient.addresses.length === 0) return '-';
            const addr = patient.addresses[0];
            return `${addr.street || ''} ${addr.number || ''}, ${addr.city || ''} - ${addr.state || ''}`.trim();
        }
    }
};

export default patientsService;